import { useCallback, useState, useEffect } from "react";
import axios from "axios";
import {
  ShieldCheck, ShieldWarning, ShieldSlash, Check, X, SpinnerGap,
  Eye, Robot, Warning, CheckCircle, XCircle, Funnel
} from "@phosphor-icons/react";
import { useLang } from "../contexts/LanguageContext";
import { toast } from "sonner";
import { promptDialog } from "../lib/uiDialogs";

import { API_BASE as API } from "../lib/apiBase";

export default function FirewallPage() {
  const [submissions, setSubmissions] = useState([]);
  const { lang, t } = useLang();
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = filter !== "all" ? `?status=${filter}` : "";
      const { data } = await axios.get(`${API}/api/admin/questionnaire-submissions${params}`, { withCredentials: true });
      setSubmissions(data);
    } catch {}
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    await axios.put(`${API}/api/admin/questionnaire-submissions/${id}/approve`, {}, { withCredentials: true });
    load();
  };

  const block = async (id) => {
    const reason = await promptDialog("Raison du blocage:");
    if (reason !== null) {
      await axios.put(`${API}/api/admin/questionnaire-submissions/${id}/block`, { reason }, { withCredentials: true });
      load();
    }
  };

  const STATUS_CFG = {
    approved: { icon: CheckCircle, color: "text-teal-500", bg: "bg-teal-50 dark:bg-teal-900/20", label: "Approuve" },
    review_required: { icon: Warning, color: "text-amber-500", bg: "bg-amber-50 dark:bg-amber-900/20", label: "A verifier" },
    blocked: { icon: XCircle, color: "text-red-500", bg: "bg-red-50 dark:bg-red-900/20", label: "Bloque" },
    pending_review: { icon: Eye, color: "text-blue-500", bg: "bg-blue-50 dark:bg-blue-900/20", label: "En attente" },
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="firewall-page">
      <div>
        <h1 className="text-2xl font-bold font-['Outfit'] flex items-center gap-2"><ShieldCheck size={24} className="text-blue-500" /> Firewall IA</h1>
        <p className="text-sm text-zinc-500 mt-1">Vérification des inscriptions et détection des concurrents</p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        {[
          { value: "all", label: "Tous", count: submissions.length },
          { value: "review_required", label: "A verifier" },
          { value: "blocked", label: "Bloques" },
          { value: "approved", label: "Approuves" },
        ].map(f => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filter === f.value ? "bg-blue-600 text-white" : "bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-200 dark:hover:bg-zinc-700"}`}
            data-testid={`filter-${f.value}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><SpinnerGap size={32} className="animate-spin text-blue-500" /></div>
      ) : submissions.length === 0 ? (
        <div className="text-center py-16">
          <ShieldCheck size={48} className="mx-auto mb-4 text-zinc-300" />
          <p className="text-zinc-500">Aucune soumission</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="submissions-list">
          {submissions.map(sub => {
            const cfg = STATUS_CFG[sub.status] || STATUS_CFG.pending_review;
            const Icon = cfg.icon;
            const isExpanded = expanded === sub.id;
            return (
              <div key={sub.id} className={`bg-card border rounded-xl overflow-hidden ${sub.status === "blocked" ? "border-red-200 dark:border-red-800" : sub.status === "review_required" ? "border-amber-200 dark:border-amber-800" : "border-border"}`} data-testid={`submission-${sub.id}`}>
                <div className="px-5 py-4 flex items-center justify-between cursor-pointer" onClick={() => setExpanded(isExpanded ? null : sub.id)}>
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg ${cfg.bg} flex items-center justify-center`}>
                      <Icon size={18} className={cfg.color} weight="fill" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{sub.user_name || sub.user_email}</span>
                        <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-zinc-500 mt-0.5">
                        <span>{sub.answers?.company_name || "N/A"}</span>
                        <span>-</span>
                        <span>Score risque: {sub.risk_score}/100</span>
                        {sub.risk_flags?.length > 0 && <span className="text-red-400">({sub.risk_flags.length} flags)</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {sub.status !== "approved" && (
                      <button onClick={(e) => { e.stopPropagation(); approve(sub.id); }} className="px-3 py-1.5 bg-teal-600 text-white text-xs rounded-lg hover:bg-teal-500" data-testid={`approve-${sub.id}`}>
                        <Check size={14} className="inline mr-1" />Approuver
                      </button>
                    )}
                    {sub.status !== "blocked" && (
                      <button onClick={(e) => { e.stopPropagation(); block(sub.id); }} className="px-3 py-1.5 bg-red-600 text-white text-xs rounded-lg hover:bg-red-500" data-testid={`block-${sub.id}`}>
                        <X size={14} className="inline mr-1" />Bloquer
                      </button>
                    )}
                  </div>
                </div>

                {isExpanded && (
                  <div className="px-5 pb-5 border-t border-border">
                    {/* Answers */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                      {Object.entries(sub.answers || {}).map(([key, val]) => (
                        <div key={key} className="bg-zinc-50 dark:bg-zinc-800/30 rounded-lg p-3">
                          <div className="text-[10px] font-bold text-zinc-400 uppercase mb-0.5">{key.replace(/_/g, " ")}</div>
                          <div className="text-sm">{Array.isArray(val) ? val.join(", ") : String(val)}</div>
                        </div>
                      ))}
                    </div>

                    {/* Risk flags */}
                    {sub.risk_flags?.length > 0 && (
                      <div className="mt-3">
                        <div className="text-xs font-bold text-red-500 mb-1">Drapeaux de risque</div>
                        <div className="flex flex-wrap gap-1">
                          {sub.risk_flags.map((f, i) => (
                            <span key={i} className="px-2 py-0.5 bg-red-50 dark:bg-red-900/20 text-red-600 text-[11px] rounded">{f.replace(/_/g, " ")}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* AI Analysis */}
                    {sub.ai_analysis && (
                      <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg">
                        <div className="flex items-center gap-1.5 text-xs font-bold text-blue-600 mb-1">
                          <Robot size={14} /> Analyse IA
                        </div>
                        <p className="text-sm text-blue-900 dark:text-blue-300">{sub.ai_analysis.analysis}</p>
                        <div className="flex gap-2 mt-2">
                          <span className="text-[10px] px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700">Niveau: {sub.ai_analysis.risk_level}</span>
                          <span className="text-[10px] px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700">Recommandation: {sub.ai_analysis.recommendation}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
